// Phase II · Wave E.1a · Currency context.
//
// Auto-derives the display currency from the current Wave D locale on
// first mount, then lets the user override via the ``CurrencySwitcher``
// component. Preference persists in cookie ``glms_currency`` (1 year).
//
// Zero-touch wrt LocaleSwitcher · lives alongside, never replaces.
import React, {
    createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import Cookies from "js-cookie";

import {
    DEFAULT_CURRENCY, SUPPORTED_CURRENCIES, currencyForLocale, formatPrice,
} from "@/i18n/currencies";

const COOKIE_NAME = "glms_currency";
const COOKIE_TTL_DAYS = 365;

const CurrencyContext = createContext({
    currency: DEFAULT_CURRENCY,
    setCurrency: () => {},
    format: (a) => String(a),
    locale: null,
});

function readCookieCurrency() {
    try {
        const v = Cookies.get(COOKIE_NAME);
        if (v && SUPPORTED_CURRENCIES.includes(v)) return v;
    } catch { /* storage disabled */ }
    return null;
}

function readLocaleFromCookieOrUrl() {
    try {
        const v = Cookies.get("glms_locale");
        if (v) return v;
    } catch { /* noop */ }
    if (typeof window !== "undefined" && window.location) {
        const m = window.location.pathname.match(
            /^\/([A-Za-z]{2,3}(?:-[A-Za-z]{2,4})?)(\/|$)/,
        );
        if (m) return m[1];
    }
    return null;
}

export function CurrencyProvider({ children }) {
    const [currency, setCurrencyState] = useState(() => {
        return readCookieCurrency() || currencyForLocale(readLocaleFromCookieOrUrl());
    });

    // Keep local state in sync when the user switches locale and has
    // never set an explicit currency preference.
    useEffect(() => {
        if (readCookieCurrency()) return;
        const derived = currencyForLocale(readLocaleFromCookieOrUrl());
        if (derived && derived !== currency) {
            setCurrencyState(derived);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setCurrency = useCallback((next) => {
        if (!next || !SUPPORTED_CURRENCIES.includes(next)) return;
        try {
            Cookies.set(COOKIE_NAME, next, {
                expires: COOKIE_TTL_DAYS, path: "/",
                sameSite: "lax", secure: true,
            });
        } catch { /* storage disabled */ }
        setCurrencyState(next);
    }, []);

    const format = useCallback(
        (amount, overrideCurrency) =>
            formatPrice(amount, overrideCurrency || currency),
        [currency],
    );

    const value = useMemo(
        () => ({ currency, setCurrency, format,
                   locale: readLocaleFromCookieOrUrl() }),
        [currency, setCurrency, format],
    );

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    return useContext(CurrencyContext);
}

export { CurrencyContext };
